import { ContentDownload } from '@fs/zion-icon'
import { useHistory, useRouteMatch } from '@fs/zion-router'
import { Button, Row, H5 } from '@fs/zion-ui'
import { usePageView } from '@fs/zion-analytics'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { usePageTitle } from '../app/PageTitleContext'
import usePatron from '../../hooks/artifacts/usePatron'
import usePatronArtifacts from '../../hooks/artifacts/usePatronArtifacts'
import { getIdElement } from '../../util/StringUtil'
import { CisLink } from '../app/Links'
import ArtifactCard from './ArtifactCard'
import ArtifactCardGrid from './ArtifactCardGrid'
import SearchBar from './SearchBar'
import DeleteAllPatronArtifactsButton from './button/DeleteAllPatronArtifactsButton'
import RestrictAllPatronArtifactsButton from './button/RestrictAllPatronArtifactsButton'
import CategoryFilter from './filter/CategoryFilter'
import { DEFAULT_PAGE_SIZE, SCREENING_STATE } from './util/artifactsConstants'
import DmcPaginator from '../common/DmcPaginator'
import StatusFilter from './filter/StatusFilter'
import SelectedArtifactsActions from './SelectedArtifactsActions'

export default function PatronArtifacts() {
  usePageView({ page_detail: 'DMConsole:PatronArtifacts' })
  const MAX_RESULTS = 500
  const history = useHistory()
  const match = useRouteMatch()
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.artifacts.patron'))
  const artifactCategory = match.params.artifactCategory ? match.params.artifactCategory : 'any'
  const userQuery = match.params.userQuery ? match.params.userQuery : ''
  const [index, setIndex] = useState(0)
  const [pageCount, setPageCount] = useState(DEFAULT_PAGE_SIZE)
  const [checkedArtifacts, setCheckedArtifacts] = useState([])
  const [selectAllArtifacts, setSelectAllArtifacts] = useState(false)
  const queryPrefixAndId = userQuery.split('.')
  const queryPrefix =
    queryPrefixAndId.length > 2 ? `${queryPrefixAndId[0]}.${queryPrefixAndId[1]}` : queryPrefixAndId[0]
  const queryPatronId = queryPrefixAndId.length > 2 ? queryPrefixAndId[2] : queryPrefixAndId[1]
  // Hide thumbnails by default, since this can display restricted artifacts
  // This preference can be set in the route query params (to persist setting when page/filter changes)
  const { patron } = usePatron(userQuery, false)
  const [status, setStatus] = useState('default')
  // Fetch artifacts uploaded by the patron
  const { artifacts, totalArtifactsSpanningPages, refetch } = usePatronArtifacts({
    userQuery,
    artifactCategory: artifactCategory.toUpperCase(),
    paginatorOffset: index * pageCount,
    paginatorCount: pageCount,
    artifactStatus: status,
    patron,
    setIndex,
  })

  useEffect(() => {
    setCheckedArtifacts([])
    setSelectAllArtifacts(false)
  }, [index])

  const handleStatusSelected = (value) => {
    setStatus(value)
    setCheckedArtifacts([])
  }

  const onCheckboxChange = (artifactId, checked) => {
    setCheckedArtifacts((prev) => {
      if (checked) {
        return [...prev, artifactId]
      }
      return prev.filter((aId) => aId !== artifactId)
    })
  }

  let patronArtifactsContent
  if (artifacts && artifacts.length > 0) {
    const artifactCardGrid = (
      <ArtifactCardGrid>
        {artifacts.map((artifact) => {
          if (status === 'restricted') {
            artifact.screeningState = SCREENING_STATE.RESTRICTED
          }
          if (status === 'approved') {
            artifact.screeningState = SCREENING_STATE.APPROVED
          }
          if (status === 'tombstoned') {
            artifact.screeningState = SCREENING_STATE.DELETED
          }
          return (
            <ArtifactCard
              key={artifact.id}
              artifact={artifact}
              refetchArtifacts={refetch}
              selectable
              checked={checkedArtifacts.includes(artifact.id)}
              onCheckboxChange={onCheckboxChange}
            />
          )
        })}
      </ArtifactCardGrid>
    )
    patronArtifactsContent =
      status === 'tombstoned' ? (
        artifactCardGrid
      ) : (
        <DmcPaginator
          maxPageCount={MAX_RESULTS}
          length={totalArtifactsSpanningPages}
          pageCount={pageCount}
          setPageCount={setPageCount}
          index={index}
          setIndex={setIndex}
          topAndBottom
        >
          {artifactCardGrid}
        </DmcPaginator>
      )
  }
  let patronContent = null
  if (patron) {
    patronContent = (
      <Row alignY="middle" gutters="xs">
        {patron.name && (
          <Row gutters="xs">
            <span>{`${t('name', 'Name')}: ${patron.name ? patron.name : ''}`}</span>
            <strong>|</strong>
          </Row>
        )}
        {patron.userName && (
          <Row gutters="xs">
            <span>{`${t('userName', 'Username')}: ${patron.userName ? patron.userName : ''}`}</span>
            <strong>|</strong>
          </Row>
        )}
        {patron.id && (
          <Row gutters="xs">
            <span>{`${t('artifacts.patronId', 'Patron ID')}: ${patron.id}`}</span>
            <strong>|</strong>
          </Row>
        )}
        {patron.cisUserId && (
          <Row gutters="xs">
            <div>
              <span>{`${t('artifacts.cisId', 'CIS ID')}: `}</span>
              {patron.cisUserId.startsWith('cis.user') ? (
                <CisLink userId={getIdElement(patron.cisUserId)} />
              ) : (
                patron.cisUserId
              )}
            </div>
          </Row>
        )}
      </Row>
    )
  }
  const nonRestrictedArtifacts = []
  if (artifacts) {
    artifacts.forEach((artifact) => {
      if (artifact.screeningState !== SCREENING_STATE.RESTRICTED) {
        nonRestrictedArtifacts.push(artifact)
      }
    })
  }
  let actionsAndFilters = null
  if (patron && artifacts && artifacts.length > 0) {
    actionsAndFilters = (
      <Row gutters="nano">
        <Button
          alignY="nextToInput"
          emphasis="medium"
          onClick={() => history.push(`/artifacts/patronDownload/${userQuery}/${status === 'tombstoned'}`)}
          Icon={ContentDownload}
        >
          {`${t('artifacts.downloadAllArtifacts', 'Download All')} (${totalArtifactsSpanningPages})`}
        </Button>
        {status !== 'tombstoned' && (
          <Row gutters="nano">
            <RestrictAllPatronArtifactsButton
              alignY="nextToInput"
              refetch={refetch}
              patronId={patron.id}
              patronUserName={patron.userName}
              totalArtifactsSpanningPages={totalArtifactsSpanningPages}
              disabled={nonRestrictedArtifacts.length === 0}
            />
            <DeleteAllPatronArtifactsButton
              alignY="nextToInput"
              totalArtifactsSpanningPages={totalArtifactsSpanningPages}
              patronId={patron.id}
              patronUserName={patron.userName}
              refetch={refetch}
              disabled={!artifacts || artifacts.length === 0}
            />
          </Row>
        )}
        <span growX />
        <CategoryFilter
          value={artifactCategory}
          onChange={(newCategory) => history.push(`/artifacts/patron/${userQuery}/${newCategory}`)}
        />
        <StatusFilter onChange={handleStatusSelected} value={status} />
      </Row>
    )
  }
  if (artifacts && artifacts.length === 0) {
    actionsAndFilters = (
      <Row>
        <H5 growX supplementary>
          {t('artifacts.noArtifactsFound', 'No artifacts found')}
        </H5>
        <CategoryFilter
          value={artifactCategory}
          onChange={(newCategory) => history.push(`/artifacts/patron/${userQuery}/${newCategory}`)}
        />
        <StatusFilter onChange={handleStatusSelected} value={status} />
      </Row>
    )
  }
  if (!artifacts) {
    actionsAndFilters = (
      <Row alignX="end">
        <StatusFilter onChange={handleStatusSelected} value={status} />
      </Row>
    )
  }
  let selectedArtifactsActions
  if (patron && artifacts && artifacts.length > 0) {
    selectedArtifactsActions = (
      <SelectedArtifactsActions
        artifacts={artifacts}
        status={status}
        checkedArtifacts={checkedArtifacts}
        setCheckedArtifacts={setCheckedArtifacts}
        refetch={refetch}
        patron={patron}
        selectAllArtifacts={selectAllArtifacts}
        setSelectAllArtifacts={setSelectAllArtifacts}
      />
    )
  }

  return (
    <>
      <SearchBar defaultQuery={queryPatronId} defaultPrefix={`${queryPrefix}.`} />
      {patronContent}
      {actionsAndFilters}
      {selectedArtifactsActions}
      {patronArtifactsContent}
    </>
  )
}
